import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Play, 
  Check, 
  X, 
  Clock, 
  AlertCircle,
  Filter,
  Video
} from 'lucide-react'
import { api } from '../services/api'
import { VideoPlayer } from '../components/VideoPlayer'
import { ActionModal } from '../components/ActionModal'
import { Pagination } from '../components/Pagination'

interface VideoModeration {
  id: string
  user_id: string
  upload_id: string
  status: 'pending' | 'approved' | 'rejected'
  rejection_reason?: string
  reviewed_at?: string
  created_at: string
  updated_at?: string
  proposed_video_url: string
}

interface VideoModerationListResponse {
  items: VideoModeration[]
  total: number
  limit: number
  offset: number
}

const statusColors = {
  pending: 'badge-warning',
  approved: 'badge-success',
  rejected: 'badge-danger',
}

const statusIcons = {
  pending: Clock,
  approved: Check,
  rejected: X,
}

export function VideosPage() {
  const [selectedVideo, setSelectedVideo] = useState<VideoModeration | null>(null)
  const [showPlayer, setShowPlayer] = useState(false)
  const [showActionModal, setShowActionModal] = useState(false)
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null)
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<VideoModerationListResponse>({
    queryKey: ['videos', statusFilter, offset, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      })
      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }
      
      const response = await api.get(`/admin/videos?${params}`)
      return response.data
    },
  })

  const approveMutation = useMutation({
    mutationFn: async ({ videoId }: { videoId: string }) => {
      await api.post(`/admin/videos/${videoId}/approve`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] })
      setShowActionModal(false)
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async ({ videoId, reason }: { videoId: string; reason: string }) => {
      await api.post(`/admin/videos/${videoId}/reject`, { reason })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] })
      setShowActionModal(false)
    },
  })

  const handleApprove = (video: VideoModeration) => {
    setSelectedVideo(video)
    setActionType('approve')
    setShowActionModal(true)
  }

  const handleReject = (video: VideoModeration) => {
    setSelectedVideo(video)
    setActionType('reject')
    setShowActionModal(true)
  }

  const handlePlayVideo = (video: VideoModeration) => {
    setSelectedVideo(video)
    setShowPlayer(true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading videos</h3>
            <div className="mt-2 text-sm text-red-700">
              {error instanceof Error ? error.message : 'An error occurred'}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Video Moderation</h1>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any)
                setOffset(0)
              }}
              className="input w-40"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-content">
          {data?.items.length === 0 ? (
            <div className="text-center py-12">
              <Video className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No videos found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter === 'all' ? 'No videos have been uploaded yet.' : `No ${statusFilter} videos found.`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Video
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items.map((video) => {
                    const StatusIcon = statusIcons[video.status]
                    return (
                      <tr key={video.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              onClick={() => handlePlayVideo(video)}
                              className="flex items-center text-sm text-red-600 hover:text-red-900"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              View Video
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {video.user_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`badge ${statusColors[video.status]}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {video.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(video.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {video.status === 'pending' && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleApprove(video)}
                                className="btn btn-success btn-sm"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleReject(video)}
                                className="btn btn-danger btn-sm"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Reject
                              </button>
                            </div>
                          )}
                          {video.status === 'rejected' && video.rejection_reason && (
                            <div className="text-sm text-gray-500">
                              Reason: {video.rejection_reason}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data && (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <Pagination
                total={data.total}
                limit={limit}
                offset={offset}
                onOffsetChange={setOffset}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setOffset(0)
                }}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </div>
          )}
        </div>
      </div>

      {/* Video Player Modal */}
      {showPlayer && selectedVideo && (
        <VideoPlayer
          video={selectedVideo}
          onClose={() => {
            setShowPlayer(false)
            setSelectedVideo(null)
          }}
        />
      )}

      {/* Action Modal */}
      {showActionModal && selectedVideo && actionType && (
        <ActionModal
          video={selectedVideo}
          actionType={actionType}
          onClose={() => {
            setShowActionModal(false)
            setSelectedVideo(null)
            setActionType(null)
          }}
          onApprove={approveMutation.mutate}
          onReject={rejectMutation.mutate}
          isLoading={approveMutation.isPending || rejectMutation.isPending}
        />
      )}
    </div>
  )
}
